import type { Prisma } from "@blis/prisma";

export type ResultProps = {
  resultId?: string | null;
  resultNumeric?: Prisma.Decimal | null;
  resultHla?: string[] | null;
  resultFreeText?: string | null;

  result?: {
    value: string;
    description: string;
    color: string | null;
  } | null;

  finalResultId?: string | null;
  finalResultNumeric?: Prisma.Decimal | null;
  finalResultHla?: string[] | null;
  finalResultFreeText?: string | null;

  finalResult?: {
    value: string;
    description: string;
    color: string | null;
  } | null;

  testResultSpec: {
    name: string | null;
    units: string | null;
    rangeMin: Prisma.Decimal | null;
    rangeMax: Prisma.Decimal | null;
    rangeMinInclusive: boolean;
    rangeMaxInclusive: boolean;
  };

  cancelled: boolean;
};

function resultHelpers<
  T extends Omit<
    ResultProps,
    | "finalResult"
    | "finalResultId"
    | "finalResultNumeric"
    | "finalResultHla"
    | "finalResultFreeText"
  >,
>(result: T) {
  if (!hasResult(result)) {
    return null;
  }

  // biome-ignore lint/suspicious/noShadowRestrictedNames: it's ok
  function toString(includeUnits = false) {
    let str = "";

    if (result.cancelled) {
      return "Cancelled";
    }

    if (result.result) {
      str = result.result.description;
    } else if (result.resultNumeric) {
      str = result.resultNumeric.toString();
    } else if (result.resultFreeText?.trim()) {
      str = result.resultFreeText;
    } else if (result.resultHla && result.resultHla.length > 0) {
      str = result.resultHla.join(", ");
    }

    if (includeUnits && result.testResultSpec.units) {
      str += ` ${result.testResultSpec.units}`;
    }

    return str;
  }

  function getRangeText(includeUnits = false) {
    if (!result.resultNumeric) {
      return toString(includeUnits);
    }

    const resultNum = result.resultNumeric;
    const { rangeMax, rangeMin, rangeMinInclusive, rangeMaxInclusive } =
      result.testResultSpec;

    let str = toString(false);

    if (
      rangeMin !== null &&
      rangeMin !== undefined &&
      resultNum.lessThanOrEqualTo(rangeMin)
    ) {
      if (rangeMinInclusive) {
        str = `<=${rangeMin}`;
      } else if (resultNum.lessThan(rangeMin)) {
        str = `<${rangeMin}`;
      }
    } else if (
      rangeMax !== null &&
      rangeMax !== undefined &&
      resultNum.greaterThanOrEqualTo(rangeMax)
    ) {
      if (rangeMaxInclusive) {
        str = `>=${rangeMax}`;
      } else if (resultNum.greaterThan(rangeMax)) {
        str = `>${rangeMax}`;
      }
    }

    if (includeUnits && result.testResultSpec.units) {
      str += ` ${result.testResultSpec.units}`;
    }

    return str;
  }

  function isInRange() {
    if (!result.resultNumeric) {
      return false;
    }
    const resultNum = result.resultNumeric;
    const { rangeMax, rangeMin, rangeMinInclusive, rangeMaxInclusive } =
      result.testResultSpec;

    if (
      rangeMin !== null &&
      rangeMin !== undefined &&
      (rangeMinInclusive
        ? resultNum.lessThanOrEqualTo(rangeMin)
        : resultNum.lessThan(rangeMin))
    ) {
      return false;
    }
    if (
      rangeMax !== null &&
      rangeMax !== undefined &&
      (rangeMaxInclusive
        ? resultNum.greaterThanOrEqualTo(rangeMax)
        : resultNum.greaterThan(rangeMax))
    ) {
      return false;
    }
    return true;
  }

  function isEqual(other: {
    resultId?: string | null;
    resultNumeric?: Prisma.Decimal | null;
    resultHla?: string[] | null;
    resultFreeText?: string | null;
  }) {
    // Helper to normalize string values (treat null, undefined, empty string as equivalent)
    const normalizeString = (value: string | null | undefined) => {
      return value === null || value === undefined || value.trim() === ""
        ? null
        : value;
    };

    // Helper to normalize array values (treat null, undefined, empty array as equivalent)
    const normalizeArray = (value: string[] | null | undefined) => {
      return value === null || value === undefined || value.length === 0
        ? null
        : value;
    };

    // Helper to check if two values are both null/undefined
    // biome-ignore lint/suspicious/noExplicitAny: it's ok
    const areBothEmpty = (a: any, b: any) => {
      return (a === null || a === undefined) && (b === null || b === undefined);
    };

    // Compare resultId (normalize empty strings)
    const normalizedResultId = normalizeString(result.resultId);
    const normalizedOtherResultId = normalizeString(other.resultId);
    if (normalizedResultId !== normalizedOtherResultId) {
      return false;
    }

    // Compare resultNumeric (handle null/undefined cases)
    if (areBothEmpty(result.resultNumeric, other.resultNumeric)) {
      // Both are null/undefined, they're equal
    } else if (result.resultNumeric && other.resultNumeric) {
      if (!result.resultNumeric.equals(other.resultNumeric)) {
        return false;
      }
    } else {
      // One is null/undefined and the other isn't
      return false;
    }

    // Compare resultHla arrays (normalize empty arrays)
    const normalizedResultHla = normalizeArray(result.resultHla);
    const normalizedOtherHla = normalizeArray(other.resultHla);
    if (areBothEmpty(normalizedResultHla, normalizedOtherHla)) {
      // Both are null/undefined/empty, they're equal
    } else if (normalizedResultHla && normalizedOtherHla) {
      if (
        normalizedResultHla.length !== normalizedOtherHla.length ||
        !normalizedResultHla.every(
          (hla, index) => hla === normalizedOtherHla[index],
        )
      ) {
        return false;
      }
    } else {
      // One is null/undefined/empty and the other isn't
      return false;
    }

    // Compare resultFreeText (normalize empty strings)
    const normalizedResultFreeText = normalizeString(result.resultFreeText);
    const normalizedOtherFreeText = normalizeString(other.resultFreeText);
    return normalizedResultFreeText === normalizedOtherFreeText;
  }

  return { toString, getRangeText, isInRange, isEqual };
}

export function extendResult<T extends ResultProps>(result: T) {
  const $initial = resultHelpers(result);
  const $final = resultHelpers({
    ...result,
    resultId: result.finalResultId,
    resultNumeric: result.finalResultNumeric,
    resultHla: result.finalResultHla,
    result: result.finalResult,
    resultFreeText: result.finalResultFreeText,
  });

  return Object.assign({}, result, { $initial, $final });
}

type Cancellable = {
  cancelled?: boolean;
  cancelledAt?: Date | null;
  cancelledReason?: string | null;
};

export function isCancelled(order: Cancellable, result?: Cancellable) {
  return result?.cancelled || order.cancelled || false;
}

export function getCancelledReason(order: Cancellable, result?: Cancellable) {
  return result?.cancelledReason || order.cancelledReason;
}

export function getResultComment(
  order: Cancellable,
  result: Cancellable & { comment?: string | null },
) {
  const cancelledReason = getCancelledReason(order, result);
  if (cancelledReason) {
    return cancelledReason;
  }
  return result.comment || "";
}

export function hasInitialResult(
  result: Pick<
    ResultProps,
    "resultId" | "resultNumeric" | "resultHla" | "resultFreeText"
  >,
) {
  return hasResult(result);
}

export function hasFinalResult(
  result: Pick<
    ResultProps,
    | "finalResultId"
    | "finalResultNumeric"
    | "finalResultHla"
    | "finalResultFreeText"
  >,
) {
  return hasResult({
    resultId: result.finalResultId,
    resultNumeric: result.finalResultNumeric,
    resultHla: result.finalResultHla,
    resultFreeText: result.finalResultFreeText,
  });
}

function hasResult(
  result: Pick<
    ResultProps,
    "resultId" | "resultNumeric" | "resultHla" | "resultFreeText"
  >,
) {
  if (
    result.resultId ||
    result.resultNumeric ||
    result.resultFreeText?.trim()
  ) {
    return true;
  }

  if (result.resultHla && result.resultHla.length > 0) {
    return true;
  }

  return false;
}

export function getResultSample<
  TSample extends { id: number; displayId: string | null },
>(
  result: { sampleId: number | null; sample: TSample | null },
  testOrder: { samples: { sample: TSample }[] },
): TSample | null {
  if (result.sample) {
    return result.sample;
  }

  const firstAssignedSample = testOrder.samples[0]?.sample;
  if (firstAssignedSample) {
    return firstAssignedSample;
  }

  return null;
}
